import React, { useEffect, useMemo, useState } from 'react';
import Navigation from '../components/Navigation/Navigation';
import apiClient from '../utils/apiClient';
import './HistoryReviewPage.css';

// Temporary page: match song titles from Jack's old churches' selections to the library.
// Data: song-catalog/historical-selections/v2. Each tap saves one decision on the server.

type Use = [string, string, string, string]; // date, church, placement, source cell
type Item = {
  id: string; st: 'number' | 'exact' | 'propose' | 'new'; t: string; sug: string;
  sp: [string, number][]; n: number; ch: string[]; u: Use[];
  hy: [string, string, string] | null; lib: [string, string][]; pr: [string, string][];
  same: string | null; note: string;
};
type Decision = { choice: string; song_id?: number; title?: string; same_id?: string; note?: string; by?: string; reason?: string; question?: string };
type Data = { built: string; items: Item[]; library: { song_id: number; title: string }[]; decisions: { item_id: string; decision: Decision }[] };

const errorText = (e: any) => e?.response?.data?.error || 'Could not save. Check the connection and try again.';
const fmtDate = (d: string) => {
  const t = new Date(d + 'T12:00:00');
  return isNaN(t.getTime()) ? d : t.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};
const CHOICE_LABEL: Record<string, string> = {
  not_song: 'Not a song', service: 'Service music', unsure: 'Not sure yet',
};

const bucketOf = (i: Item, decisions: Record<string, Decision>) => {
  const d = decisions[i.id];
  if (!d || d.choice === 'ask') return 'todo';
  return d.by === 'claude' ? 'claude' : 'done';
};

const HistoryReviewPage: React.FC = () => {
  const [data, setData] = useState<Data | null>(null);
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState<'todo' | 'claude' | 'done'>('todo');
  const [church, setChurch] = useState('');
  const [search, setSearch] = useState('');
  const [shown, setShown] = useState(25);

  useEffect(() => {
    apiClient.get<Data>('/history-review').then(({ data }) => {
      setData(data);
      setDecisions(Object.fromEntries(data.decisions.map(d => [d.item_id, d.decision])));
    }).catch(e => setLoadError(errorText(e)));
  }, []);

  const byId = useMemo(() => Object.fromEntries((data?.items || []).map(i => [i.id, i])), [data]);
  const churches = useMemo(() => Array.from(new Set((data?.items || []).flatMap(i => i.ch))).sort(), [data]);
  const list = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.items.filter(i => {
      if (bucketOf(i, decisions) !== tab) return false;
      if (church && !i.ch.includes(church)) return false;
      if (q && !i.sp.some(([s]) => s.toLowerCase().includes(q)) && !i.lib.some(([, t]) => t.toLowerCase().includes(q))) return false;
      return true;
    }).sort((a, b) => b.n - a.n);
  }, [data, decisions, tab, church, search]);

  const counts = useMemo(() => {
    const items = data?.items || [];
    return {
      todo: items.filter(i => bucketOf(i, decisions) === 'todo').length,
      claude: items.filter(i => bucketOf(i, decisions) === 'claude').length,
      done: items.filter(i => bucketOf(i, decisions) === 'done').length,
    };
  }, [data, decisions]);

  const save = async (id: string, d: Decision | { choice: 'clear' }) => {
    const { data: res } = await apiClient.put(`/history-review/decisions/${id}`, d);
    setDecisions(prev => {
      const next = { ...prev };
      if (res.decision) next[id] = res.decision; else delete next[id];
      return next;
    });
  };

  if (loadError) return <><Navigation /><main className="hr"><p className="hr-error" role="alert">{loadError}</p></main></>;
  if (!data) return <><Navigation /><main className="hr"><p className="hr-muted">Loading the old selections…</p></main></>;

  return <><Navigation /><main className="hr">
    <header className="hr-head">
      <h1>Old selections: song matching</h1>
      <p className="hr-muted">Titles from St Luke's Renton, Emmanuel, Immanuel, Los Gatos UMC, Our Lady of Guadalupe and St Andrew's by the Sea. Each choice saves as you tap it. Nothing changes in the library or the services until the import runs.</p>
    </header>
    <nav className="hr-tabs" aria-label="Review lists">
      {([['todo', 'Questions for you'], ['claude', 'Decided by Claude'], ['done', 'Decided by you']] as const).map(([k, label]) =>
        <button key={k} id={`hr-tab-${k}`} className={tab === k ? 'on' : ''} aria-pressed={tab === k} onClick={() => { setTab(k); setShown(25); }}>
          {label} <span className="hr-count">{counts[k]}</span>
        </button>)}
    </nav>
    <div className="hr-filters">
      <label htmlFor="hr-church">Church</label>
      <select id="hr-church" value={church} onChange={e => { setChurch(e.target.value); setShown(25); }}>
        <option value="">All churches</option>
        {churches.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      <label htmlFor="hr-search">Search</label>
      <input id="hr-search" type="search" value={search} placeholder="A word from the title" onChange={e => { setSearch(e.target.value); setShown(25); }} />
    </div>
    {tab === 'claude' && <p className="hr-muted">Each of these was checked against the hymnals, your library and the source sheets. They go in as shown unless you change one.</p>}
    {list.length === 0 && <p className="hr-muted">Nothing here{church || search ? ' with these filters' : ''}.</p>}
    <ol className="hr-list">
      {list.slice(0, shown).map(i => <ItemCard key={i.id} item={i} decision={decisions[i.id]} byId={byId}
        library={data.library} save={save} />)}
    </ol>
    {list.length > shown && <button className="hr-more" onClick={() => setShown(s => s + 25)}>Show 25 more of {list.length - shown}</button>}
    <p className="hr-muted hr-foot">Built {data.built.replace('T', ' ')} from the extracted sheets.</p>
  </main></>;
};

const ItemCard: React.FC<{
  item: Item; decision?: Decision; byId: Record<string, Item>;
  library: { song_id: number; title: string }[];
  save: (id: string, d: any) => Promise<void>;
}> = ({ item, decision, byId, library, save }) => {
  const [mode, setMode] = useState<'' | 'new' | 'pick'>('');
  const [editing, setEditing] = useState(false);
  const [newTitle, setNewTitle] = useState(item.sug);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const run = async (d: any) => {
    setBusy(true); setErr('');
    try { await save(item.id, d); setMode(''); setEditing(false); } catch (e) { setErr(errorText(e)); } finally { setBusy(false); }
  };
  const hits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return library.filter(s => s.title.toLowerCase().includes(q)).slice(0, 8);
  }, [query, library]);
  const asking = decision?.choice === 'ask';
  const showDecision = decision && !asking && !editing;
  const others = item.sp.slice(1).map(([s]) => s).filter(s => s.trim());

  const summary = decision && (
    decision.choice === 'song' ? <>Is <strong>{decision.title}</strong> in the library</> :
    decision.choice === 'new' ? <>New song: <strong>{decision.title}</strong></> :
    decision.choice === 'same' ? <>Same song as <strong>{byId[decision.same_id || '']?.t || 'another title'}</strong></> :
    <strong>{CHOICE_LABEL[decision.choice] || decision.choice}</strong>);

  return <li className={`hr-card${decision ? ' decided' : ''}`}>
    <div className="hr-title-row">
      <h2>{item.t || '(number only)'}</h2>
      <span className="hr-uses" title="Times used">{item.n}×</span>
    </div>
    <p className="hr-churches">{item.ch.join(' · ')}</p>
    {others.length > 0 && <p className="hr-small">Also written: {others.slice(0, 5).join(' · ')}{others.length > 5 ? ' …' : ''}</p>}
    {item.hy && <p className="hr-small hr-rubric">{item.hy[0]}{item.hy[1] ? `: ${item.hy[1]}` : ''}{item.hy[2] ? ` (tune ${item.hy[2]})` : ''}</p>}
    <ul className="hr-uselist">
      {item.u.slice(0, 3).map((u, k) => <li key={k}><span className="hr-date">{fmtDate(u[0])}</span> {u[1]}, {u[2]}</li>)}
      {item.n > 3 && <li className="hr-small">and {item.n - 3} more</li>}
    </ul>
    {item.note && <p className="hr-small hr-note">{item.note}</p>}

    {asking && <p className="hr-question">{decision!.question}</p>}
    {decision?.reason && <p className="hr-small hr-reason">{decision.by === 'claude' ? 'Why: ' : ''}{decision.reason}</p>}
    {showDecision ? <div className="hr-decided">
      <span>{summary}</span>
      <button disabled={busy} onClick={() => setEditing(true)}>Change</button>
    </div> : <div className="hr-actions">
      {item.lib.map(([id, t]) => <button key={id} className="hr-pick" disabled={busy} onClick={() => run({ choice: 'song', song_id: Number(id) })}>It is “{t}”</button>)}
      {item.pr.map(([id, t]) => {
        const tune = t.endsWith(' (its tune is called this)');
        const title = tune ? t.slice(0, -' (its tune is called this)'.length) : t;
        return <button key={id} className="hr-pick" disabled={busy} onClick={() => run({ choice: 'song', song_id: Number(id) })}>
          It is “{title}”{tune && <span className="hr-small"> (sung to a tune by this name)</span>}</button>;
      })}
      {item.same && byId[item.same] && <button className="hr-pick" disabled={busy} onClick={() => run({ choice: 'same', same_id: item.same })}>Same song as “{byId[item.same].t}”</button>}
      <div className="hr-row">
        {<button className="hr-primary" disabled={busy} onClick={() => setMode(mode === 'new' ? '' : 'new')}>New song</button>}
        <button disabled={busy} onClick={() => setMode(mode === 'pick' ? '' : 'pick')}>Another library song</button>
        {<>
          <button disabled={busy} onClick={() => run({ choice: 'service' })}>Service music</button>
          <button disabled={busy} onClick={() => run({ choice: 'not_song' })}>Not a song</button>
          <button disabled={busy} onClick={() => run({ choice: 'unsure' })}>Not sure</button>
        </>}
        {editing && <button disabled={busy} onClick={() => setEditing(false)}>Keep as it was</button>}
      </div>
      {mode === 'new' && <form className="hr-inline" onSubmit={e => { e.preventDefault(); run({ choice: 'new', title: newTitle }); }}>
        <label htmlFor={`hr-new-${item.id}`}>Title for the new song</label>
        <input id={`hr-new-${item.id}`} value={newTitle} onChange={e => setNewTitle(e.target.value)} />
        <button className="hr-primary" disabled={busy || !newTitle.trim()}>Save new song</button>
      </form>}
      {mode === 'pick' && <div className="hr-inline">
        <label htmlFor={`hr-find-${item.id}`}>Find the song in the library</label>
        <input id={`hr-find-${item.id}`} type="search" value={query} autoFocus placeholder="Type part of the title" onChange={e => setQuery(e.target.value)} />
        <ul className="hr-hits">
          {hits.map(s => <li key={s.song_id}><button disabled={busy} onClick={() => run({ choice: 'song', song_id: s.song_id })}>{s.title}</button></li>)}
          {query.trim().length >= 2 && hits.length === 0 && <li className="hr-small">No library song has that in its title.</li>}
        </ul>
      </div>}
    </div>}
    {err && <p className="hr-error" role="alert">{err}</p>}
  </li>;
};

export default HistoryReviewPage;
